import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EnumAnswerStatus, EnumConversationStatus } from '../enums';
import { ReportChatHistoryRecord, ReportChatList } from '../types';
import { stopConversation } from './asyncThunks';
import { generateRandomString } from '@gainer-ui/ai-bi-chat';

export interface ReportChatState {
  /** 页面级loading状态 */
  loading: boolean;
  /** 侧边栏折叠状态 */
  collapse: boolean;

  chatList: ReportChatList.DataItem[];
  /** 对话列表key，用来重新请求数据 */
  chatListKey: string;
  /** 当前对话id */
  conversationId?: string;
  /** 当前轮次id */
  roundId?: string;
  /** 当前输入框内容 */
  input: string;
  /** 回答状态, 主要用来控制交互动画，如停止按钮、打字效果，chatData中的is_finished主要表示的是对话内容已结束 */
  answerStatus: EnumAnswerStatus;
  /** 是否正在重连 */
  reconnecting: boolean;
  abortController?: AbortController;
  /** 当前正在对话的数据 */
  currentChat: ReportChatList.DataItem | null;

  /** 历史对话记录 */
  conversationList: ReportChatHistoryRecord.DataItem[];
  loadingConversation: boolean;
  /** 历史对话记录key，用来重新请求数据 */
  conversationKey: string;

  /** 当前右侧选择的报告id */
  currentReportId: string | null;
  /** 当前右侧展示的报告信息 */
  currentReportInfo: ReportChatList.ReportInfo | null;
  /** 当前引用的报告id */
  referenceReportId: string | null;

  /** 主题id */
  topicId: number | null;
}

const initialState: ReportChatState = {
  loading: false,
  collapse: true,
  chatList: [],
  chatListKey: '',
  conversationId: undefined,
  roundId: undefined,
  input: '',
  answerStatus: EnumAnswerStatus.completed,
  currentChat: null,
  reconnecting: false,

  conversationList: [],
  loadingConversation: false,
  conversationKey: '',

  currentReportId: null,
  currentReportInfo: null,
  referenceReportId: null,

  topicId: null,
};

/** 结束对话通用操作 */
const cancel = (state: ReportChatState) => {
  if (state.currentChat) {
    state.currentChat = {
      ...state.currentChat,
      status: EnumConversationStatus.done,
      is_finished: true,
      intermediate_steps:
        state.currentChat.intermediate_steps?.map(v => ({
          ...v,
          end: true,
        })) || [],
    };
  }
  state.abortController?.abort();
  state.abortController = undefined;
};

/** 对话生成报告 */
const reportSlice = createSlice({
  name: 'report',
  initialState,
  reducers: {
    /** 设置当前对话id */
    setConversationId: (state, action: PayloadAction<string | undefined>) => {
      if (!action.payload) {
        state.topicId = null;
      }
      if (state.conversationId === action.payload) return;
      state.chatList = [];
      if (state.abortController) {
        cancel(state);
      }
      const found = state.conversationList.find(
        v => v.conversation_id === action.payload,
      );

      if (found && found.topic) {
        state.topicId = Number(found.topic);
      }
      state.currentChat = null;
      state.currentReportId = null;
      state.currentReportInfo = null;
      state.referenceReportId = null;
      state.answerStatus = EnumAnswerStatus.completed;
      state.conversationId = action.payload;
    },
    /** 设置当前轮次id */
    setRoundId: (state, action: PayloadAction<string | undefined>) => {
      state.roundId = action.payload;
    },
    /** 设置主题id */
    setTopicId: (state, action: PayloadAction<number | null>) => {
      state.topicId = action.payload;
    },
    /** 设置回答状态 */
    setAnswerStatus: (state, action: PayloadAction<EnumAnswerStatus>) => {
      state.answerStatus = action.payload;
    },
    /** 设置是否正在重连 */
    setReconnecting: (state, action: PayloadAction<boolean>) => {
      state.reconnecting = action.payload;
    },
    /** 设置取消控制器 */
    setAbortController: (
      state,
      action: PayloadAction<AbortController | undefined>,
    ) => {
      state.abortController = action.payload;
    },
    /** 设置侧边栏折叠状态 */
    setCollapse: (state, action: PayloadAction<boolean>) => {
      state.collapse = action.payload;
    },
    /** 更新输入框内容 */
    setInput: (state, action: PayloadAction<string>) => {
      state.input = action.payload;
    },
    /** 设置历史记录列表 */
    setConversationList: (
      state,
      action: PayloadAction<ReportChatHistoryRecord.DataItem[]>,
    ) => {
      state.conversationList = action.payload;
    },
    /** 追加历史记录 */
    appendConversationList: (
      state,
      action: PayloadAction<ReportChatHistoryRecord.DataItem[]>,
    ) => {
      state.conversationList.push(...action.payload);
    },
    /** 设置当前会话列表 */
    setChatList: (state, action: PayloadAction<ReportChatList.DataItem[]>) => {
      state.chatList = action.payload;
    },
    /** 追加当前会话列表 */
    appendChatList: (
      state,
      action: PayloadAction<ReportChatList.DataItem[]>,
    ) => {
      state.chatList.unshift(...action.payload);
    },
    /** 设置当前会话 */
    setCurrentChat: (
      state,
      action: PayloadAction<ReportChatList.DataItem | null>,
    ) => {
      state.currentChat = action.payload;
    },
    /** 设置当前报告id */
    setCurrentReportId: (state, action: PayloadAction<string | null>) => {
      state.currentReportId = action.payload;
    },
    /** 设置当前报告信息 */
    setCurrentReportInfo: (
      state,
      action: PayloadAction<ReportChatList.ReportInfo | null>,
    ) => {
      state.currentReportInfo = action.payload;
    },
    /** 设置当前引用报告id */
    setReferenceReportId: (state, action: PayloadAction<string | null>) => {
      state.referenceReportId = action.payload;
    },
    /** 更新对话历史记录刷新key */
    updateConversationKey: state => {
      state.conversationKey = generateRandomString(32);
    },
    /** 更新对话列表刷新key */
    updateChatListKey: state => {
      state.chatListKey = generateRandomString(32);
    },
    /** 断开sse连接 */
    cancelConversation: state => {
      cancel(state);
    },
    /** 完成对话，用于打字机结束主动调用 */
    finishConversation: state => {
      state.answerStatus = EnumAnswerStatus.completed;
      const { currentChat } = state;
      if (currentChat) {
        state.referenceReportId = currentChat.report || null;
      }
      cancel(state);
    },
  },
  extraReducers: builder => {
    // 停止对话
    builder.addCase(stopConversation.pending, state => {});
    builder.addCase(stopConversation.fulfilled, (state, action) => {
      if (action.payload) {
        if (state.currentChat) {
          state.currentChat.is_interrupted = true;
          state.currentChat.is_finished = true;
        }
        cancel(state);
        state.answerStatus = EnumAnswerStatus.completed;
      }
    });
  },
});

export const {
  setConversationId,
  setCollapse,
  setConversationList,
  appendConversationList,
  setInput,
  setRoundId,
  setTopicId,
  setAnswerStatus,
  setReconnecting,
  setAbortController,
  setChatList,
  appendChatList,
  setCurrentChat,
  setCurrentReportId,
  setCurrentReportInfo,
  setReferenceReportId,
  updateConversationKey,
  cancelConversation,
  finishConversation,
  updateChatListKey,
} = reportSlice.actions;

export default reportSlice.reducer;
